/**
 * The analytical sections of the calibration data hub. All visualizations are
 * bespoke inline/SVG (consistent with LA's radar/whisker), brand-styled, and
 * worded in plain-HR terms — no GEPA/Pareto/rollout jargon.
 */

import type {
  CalibrationAnalytics,
  CalibrationRound,
  CompetencyCoverage,
  MistakeExample,
} from "@shared/labs/level-assessment-calibration-analytics";
import { useMemo, useState } from "react";
import { C, DM, levelLabel, UI } from "../results/ui-tokens";
import { Card, Chip, fmtDate, fmtPct, Muted, Section, StatCard } from "./ui";

const oneDecimal = (n: number) => n.toFixed(1).replace(".", ",");

/** Only treat a stored URL as a clickable link when it is plainly http(s).
 *  The candidate URL comes from scraped/operator-entered data; never render an
 *  unexpected scheme (e.g. javascript:) as an href. */
function safeHttpUrl(url: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

/** Stable, scope-aware key for grouping/filtering examples by competency
 *  (an axisId alone can repeat across cross vs. a discipline). */
const exampleCompKey = (e: { disciplineLabel: string | null; axisId: string }) =>
  `${e.disciplineLabel ?? "—"}::${e.axisId}`;

// ── 1. Summary ───────────────────────────────────────────────────────────────

export function SummarySection({ a }: { a: CalibrationAnalytics }) {
  const s = a.summary;
  return (
    <Section
      testId="cal-data-summary"
      title="Resumo"
      hint="O que o modelo já aprendeu com as revisões que você fez ao final de cada avaliação."
    >
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatCard
          testId="stat-reviews"
          label="Revisões"
          value={String(s.totalReviews)}
          sub={`${s.confirmations} confirmações · ${s.corrections} correções`}
        />
        <StatCard label="Perfis avaliados" value={String(s.distinctProfiles)} />
        <StatCard
          testId="stat-agreement"
          label="Concordância"
          value={fmtPct(s.agreementRate)}
          sub="no momento da revisão"
          accent={s.agreementRate != null && s.agreementRate >= 0.7 ? C.greenFg : undefined}
        />
        <StatCard
          label="Competências cobertas"
          value={`${s.competenciesCovered}/${s.competenciesTotal}`}
          sub={s.competenciesCovered < s.competenciesTotal ? "ainda há lacunas" : "todas com dados"}
        />
        <StatCard
          label="Níveis cobertos"
          value={`${s.levelsCovered}/${s.levelsTotal}`}
          sub={`última revisão ${fmtDate(s.lastReviewedAt)}`}
        />
      </div>
      {s.orphanReviews > 0 && (
        <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 8 }}>
          + {s.orphanReviews} {s.orphanReviews === 1 ? "revisão" : "revisões"} de competências
          removidas do modelo (não contabilizadas acima).
        </div>
      )}
      {s.staleReviews > 0 && (
        <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 6 }}>
          {s.staleReviews}{" "}
          {s.staleReviews === 1 ? "revisão foi coletada" : "revisões foram coletadas"} com uma
          versão anterior dos sinais — o modelo as reavalia na próxima calibração.
        </div>
      )}
    </Section>
  );
}

// ── 2. Coverage heatmap ────────────────────────────────────────────────────────

function groupCoverage(coverage: CompetencyCoverage[]) {
  const cross = coverage.filter((c) => c.scope === "cross");
  const byDisc = new Map<string, { label: string; items: CompetencyCoverage[] }>();
  for (const c of coverage) {
    if (c.scope !== "vertical" || !c.disciplineId) continue;
    const g = byDisc.get(c.disciplineId) ?? {
      label: c.disciplineLabel ?? c.disciplineId,
      items: [],
    };
    g.items.push(c);
    byDisc.set(c.disciplineId, g);
  }
  return { cross, disciplines: Array.from(byDisc.values()) };
}

function HeatRow({
  c,
  levels,
  maxCount,
  names,
}: {
  c: CompetencyCoverage;
  levels: number[];
  maxCount: number;
  names: Record<string, string>;
}) {
  return (
    <>
      <div
        style={{ ...UI, fontSize: 13, color: C.ink, alignSelf: "center", paddingRight: 8 }}
        title={c.label}
      >
        {c.label}
      </div>
      {levels.map((lvl) => {
        const inBand = lvl >= c.levelMin && lvl <= c.levelMax;
        const count = c.levelCounts[String(lvl)] ?? 0;
        let bg: string = C.paper;
        let fg: string = C.ink50;
        if (count > 0) {
          // Always show a real count, even if the level is outside this
          // competency's band (the band may have been narrowed after the
          // sample was collected) — hiding it would lose data the sampleCount
          // chip still includes.
          const t = maxCount > 0 ? count / maxCount : 0;
          const op = 0.2 + 0.8 * t;
          bg = `rgba(243,53,75,${op.toFixed(2)})`;
          fg = op > 0.55 ? C.white : C.ink;
        } else if (!inBand) {
          bg = "transparent";
          fg = "transparent";
        }
        return (
          <div
            key={lvl}
            title={
              count > 0
                ? `${levelLabel(lvl, names)} — ${count} ${count === 1 ? "revisão" : "revisões"}${inBand ? "" : " (fora da faixa desta competência)"}`
                : inBand
                  ? `${levelLabel(lvl, names)} — sem revisões`
                  : `${levelLabel(lvl, names)} — fora da faixa desta competência`
            }
            style={{
              height: 30,
              borderRadius: 6,
              background: bg,
              border: inBand && count === 0 ? `1px solid ${C.borderSoft}` : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...DM,
              fontSize: 12,
              fontWeight: 600,
              color: fg,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {count > 0 ? count : ""}
          </div>
        );
      })}
      <div
        style={{ alignSelf: "center", paddingLeft: 8, display: "flex", justifyContent: "flex-end" }}
      >
        {c.sampleCount > 0 ? (
          <Chip
            fg={c.agreementRate != null && c.agreementRate >= 0.7 ? C.greenFg : C.ink70}
            bg={c.agreementRate != null && c.agreementRate >= 0.7 ? C.greenBg : C.ink5}
            title={`${c.sampleCount} revisões · ${fmtPct(c.agreementRate)} de concordância`}
          >
            {fmtPct(c.agreementRate)}
          </Chip>
        ) : (
          <Chip fg={C.amberFg} bg={C.amberBg}>
            sem dados
          </Chip>
        )}
      </div>
    </>
  );
}

function HeatGrid({
  rows,
  levels,
  maxCount,
  names,
}: {
  rows: CompetencyCoverage[];
  levels: number[];
  maxCount: number;
  names: Record<string, string>;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `minmax(120px, 1.3fr) repeat(${levels.length}, minmax(28px, 1fr)) auto`,
        gap: 5,
        alignItems: "center",
      }}
    >
      <div />
      {levels.map((lvl) => (
        <div
          key={lvl}
          title={levelLabel(lvl, names)}
          style={{
            ...UI,
            fontSize: 11,
            fontWeight: 600,
            color: C.ink50,
            textAlign: "center",
          }}
        >
          L{lvl}
        </div>
      ))}
      <div />
      {rows.map((c) => (
        <HeatRow
          key={`${c.scope}:${c.disciplineId ?? ""}:${c.axisId}`}
          c={c}
          levels={levels}
          maxCount={maxCount}
          names={names}
        />
      ))}
    </div>
  );
}

export function CoverageSection({ a }: { a: CalibrationAnalytics }) {
  const levels = useMemo(() => {
    const out: number[] = [];
    for (let l = a.levels.min; l <= a.levels.max; l++) out.push(l);
    return out;
  }, [a.levels.min, a.levels.max]);
  const maxCount = useMemo(() => {
    let m = 0;
    for (const c of a.coverage)
      for (const k of Object.keys(c.levelCounts)) m = Math.max(m, c.levelCounts[k] ?? 0);
    return m;
  }, [a.coverage]);
  const { cross, disciplines } = groupCoverage(a.coverage);

  return (
    <Section
      testId="cal-data-coverage"
      title="Cobertura"
      hint="Quais competências e quais níveis você já ensinou ao modelo. Quanto mais forte a cor, mais exemplos naquele nível."
    >
      <Card>
        {cross.length > 0 && (
          <div style={{ marginBottom: disciplines.length > 0 ? 22 : 0 }}>
            <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink70, marginBottom: 10 }}>
              Competências gerais
            </div>
            <HeatGrid rows={cross} levels={levels} maxCount={maxCount} names={a.levelNames} />
          </div>
        )}
        {disciplines.map((g) => (
          <div key={g.label} style={{ marginTop: 22 }}>
            <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink70, marginBottom: 10 }}>
              {g.label}
            </div>
            <HeatGrid rows={g.items} levels={levels} maxCount={maxCount} names={a.levelNames} />
          </div>
        ))}
      </Card>

      {(a.gaps.competenciesWithoutData.length > 0 || a.gaps.levelsNeverTaught.length > 0) && (
        <Card style={{ marginTop: 12, background: C.red5, borderColor: C.red20 }}>
          <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 8 }}>
            Lacunas de calibração
          </div>
          {a.gaps.competenciesWithoutData.length > 0 && (
            <div
              style={{
                marginBottom: 8,
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span style={{ ...UI, fontSize: 12.5, color: C.ink70 }}>Competências sem dados:</span>
              {a.gaps.competenciesWithoutData.map((g) => (
                <Chip key={`${g.disciplineId ?? ""}:${g.axisId}`} fg={C.ink70} bg={C.white}>
                  {g.disciplineLabel ? `${g.disciplineLabel} · ${g.label}` : g.label}
                </Chip>
              ))}
            </div>
          )}
          {a.gaps.levelsNeverTaught.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ ...UI, fontSize: 12.5, color: C.ink70 }}>
                Níveis ainda não ensinados:
              </span>
              {a.gaps.levelsNeverTaught.map((l) => (
                <Chip key={l} fg={C.ink70} bg={C.white}>
                  {levelLabel(l, a.levelNames)}
                </Chip>
              ))}
            </div>
          )}
        </Card>
      )}
    </Section>
  );
}

// ── 3. Agreement & bias (quantitative) ─────────────────────────────────────────

function DeltaDistribution({ a }: { a: CalibrationAnalytics }) {
  const dist = a.mistakes.deltaDistribution;
  const max = Math.max(1, ...dist.map((d) => d.count));
  if (dist.length === 0) return <Muted>Sem revisões com nível atribuído ainda.</Muted>;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 132 }}>
        {dist.map((d) => {
          const h = Math.round((d.count / max) * 104);
          const color = d.delta < 0 ? C.red : d.delta > 0 ? C.violet : C.green;
          const mag = Math.abs(d.delta);
          const label =
            d.delta === 0
              ? "concordou"
              : `modelo ${mag} ${mag === 1 ? "nível" : "níveis"} ${d.delta < 0 ? "acima" : "abaixo"}`;
          return (
            <div
              key={d.delta}
              title={`${d.count} ${d.count === 1 ? "avaliação" : "avaliações"} — ${label}`}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
              }}
            >
              <div
                style={{
                  ...DM,
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.ink70,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {d.count > 0 ? d.count : ""}
              </div>
              <div
                style={{
                  width: "100%",
                  maxWidth: 46,
                  height: Math.max(d.count > 0 ? 4 : 0, h),
                  background: color,
                  borderRadius: 6,
                  opacity: d.delta === 0 ? 0.9 : 0.85,
                }}
              />
              <div
                style={{ ...UI, fontSize: 11, color: C.ink50, fontVariantNumeric: "tabular-nums" }}
              >
                {d.delta > 0 ? `+${d.delta}` : d.delta}
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          ...UI,
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: C.ink50,
          marginTop: 8,
        }}
      >
        <span>← o modelo superestimava</span>
        <span>concordância</span>
        <span>o modelo subestimava →</span>
      </div>
    </div>
  );
}

export function AgreementSection({ a }: { a: CalibrationAnalytics }) {
  const m = a.mistakes;
  const withReviews = m.perCompetency.filter((p) => p.reviews > 0);
  const worstError = Math.max(0.0001, ...withReviews.map((p) => p.meanAbsDelta ?? 0));
  return (
    <Section
      testId="cal-data-agreement"
      title="Concordância e desacordos"
      hint="Com que frequência o modelo concordou com você — e, quando discordou, se tendia a estimar acima ou abaixo do nível correto. Reflete a leitura do modelo no momento de cada revisão."
    >
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
        <Card style={{ flex: "1 1 360px" }}>
          <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink70, marginBottom: 12 }}>
            Para que lado o modelo erra
          </div>
          <DeltaDistribution a={a} />
          <div style={{ ...UI, fontSize: 12.5, color: C.ink70, marginTop: 14, lineHeight: 1.55 }}>
            {m.overEstimateCount + m.underEstimateCount === 0 ? (
              "Ainda não há desacordos com nível atribuído."
            ) : (
              <>
                {m.overEstimateCount} {m.overEstimateCount === 1 ? "vez" : "vezes"} superestimou ·{" "}
                {m.underEstimateCount} {m.underEstimateCount === 1 ? "vez" : "vezes"} subestimou.
                {m.meanAbsDeltaOnDisagreements != null && (
                  <>
                    {" "}
                    Quando discorda, fica em média{" "}
                    <strong style={{ color: C.ink }}>
                      {oneDecimal(m.meanAbsDeltaOnDisagreements)}
                    </strong>{" "}
                    nível(is) de distância.
                  </>
                )}
              </>
            )}
          </div>
        </Card>

        <Card style={{ flex: "1 1 320px" }}>
          <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink70, marginBottom: 12 }}>
            Por competência
          </div>
          {withReviews.length === 0 ? (
            <Muted>Sem revisões ainda.</Muted>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {withReviews.map((p) => (
                <div
                  key={`${p.scope}:${p.disciplineId ?? ""}:${p.axisId}`}
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <div
                    style={{ ...UI, fontSize: 12.5, color: C.ink, flex: "1 1 auto", minWidth: 0 }}
                  >
                    {p.disciplineLabel ? `${p.disciplineLabel} · ${p.label}` : p.label}
                  </div>
                  <Chip
                    fg={p.agreementRate != null && p.agreementRate >= 0.7 ? C.greenFg : C.ink70}
                    bg={p.agreementRate != null && p.agreementRate >= 0.7 ? C.greenBg : C.ink5}
                  >
                    {fmtPct(p.agreementRate)}
                  </Chip>
                  <div
                    title={`Erro médio: ${oneDecimal(p.meanAbsDelta ?? 0)} nível(is)`}
                    style={{
                      width: 64,
                      height: 8,
                      background: C.ink5,
                      borderRadius: 999,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, ((p.meanAbsDelta ?? 0) / worstError) * 100)}%`,
                        height: "100%",
                        background: (p.meanAbsDelta ?? 0) > 0 ? C.red : C.green,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Section>
  );
}

// ── 4. Specific disagreements (qualitative) ─────────────────────────────────────

function DiffChip({ e }: { e: MistakeExample }) {
  if (e.modelLevel === null) {
    return (
      <Chip fg={C.ink50} bg={C.ink5} title="O modelo não cravou um nível">
        sem leitura
      </Chip>
    );
  }
  const mag = Math.abs(e.delta ?? 0);
  if (mag === 0)
    return (
      <Chip fg={C.greenFg} bg={C.greenBg}>
        sem diferença
      </Chip>
    );
  const over = e.direction === "super";
  return (
    <Chip fg={over ? C.red : C.violet} bg={over ? C.red10 : C.violetBg}>
      {mag} {mag === 1 ? "nível" : "níveis"} {over ? "acima" : "abaixo"}
    </Chip>
  );
}

export function MistakesSection({ a }: { a: CalibrationAnalytics }) {
  const [filter, setFilter] = useState<string>("all");
  const names = a.levelNames;

  const options = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of a.mistakes.examples) {
      const key = exampleCompKey(e);
      const label = e.disciplineLabel ? `${e.disciplineLabel} · ${e.label}` : e.label;
      if (!seen.has(key)) seen.set(key, label);
    }
    return Array.from(seen.entries());
  }, [a.mistakes.examples]);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? a.mistakes.examples
        : a.mistakes.examples.filter((e) => exampleCompKey(e) === filter),
    [a.mistakes.examples, filter],
  );
  const VISIBLE = 60;
  const shown = filtered.slice(0, VISIBLE);

  return (
    <Section
      testId="cal-data-mistakes"
      title="Desacordos específicos"
      hint="As avaliações em que você corrigiu o modelo. Útil para revisar onde ele ainda não acompanha o seu critério."
      right={
        options.length > 0 ? (
          <select
            data-testid="cal-data-mistakes-filter"
            value={filter}
            onChange={(ev) => setFilter(ev.target.value)}
            style={{
              ...UI,
              fontSize: 12.5,
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: C.white,
              color: C.ink,
            }}
          >
            <option value="all">Todas as competências</option>
            {options.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        ) : undefined
      }
    >
      <Card style={{ padding: 0, overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 20 }}>
            <Muted>Nenhuma correção registrada — o modelo concordou com todas as revisões.</Muted>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {shown.map((e, i) => (
              <div
                key={`${e.axisId}:${e.createdAt}:${e.candidateName ?? "?"}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 18px",
                  borderTop: i === 0 ? "none" : `1px solid ${C.borderSoft}`,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                  <div style={{ ...UI, fontSize: 13.5, color: C.ink, fontWeight: 500 }}>
                    {safeHttpUrl(e.linkedinUrl) ? (
                      <a
                        href={safeHttpUrl(e.linkedinUrl) ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: C.ink,
                          textDecoration: "underline",
                          textDecorationStyle: "dotted",
                        }}
                      >
                        {e.candidateName ?? "Perfil"}
                      </a>
                    ) : (
                      (e.candidateName ?? "Perfil")
                    )}
                  </div>
                  <div style={{ ...UI, fontSize: 12, color: C.ink50 }}>
                    {e.disciplineLabel ? `${e.disciplineLabel} · ${e.label}` : e.label}
                  </div>
                </div>
                <div style={{ ...UI, fontSize: 12.5, color: C.ink70, minWidth: 150 }}>
                  modelo <strong style={{ color: C.ink }}>{levelLabel(e.modelLevel, names)}</strong>{" "}
                  → você{" "}
                  <strong style={{ color: C.ink }}>{levelLabel(e.correctedLevel, names)}</strong>
                </div>
                <DiffChip e={e} />
                <div
                  style={{ ...UI, fontSize: 12, color: C.ink50, minWidth: 88, textAlign: "right" }}
                >
                  {fmtDate(e.createdAt)}
                </div>
                {e.note && (
                  <div
                    style={{
                      ...UI,
                      fontSize: 12,
                      color: C.ink50,
                      flexBasis: "100%",
                      fontStyle: "italic",
                    }}
                    title={e.note}
                  >
                    “{e.note}”
                  </div>
                )}
              </div>
            ))}
            {filtered.length > VISIBLE && (
              <div
                style={{
                  ...UI,
                  fontSize: 12,
                  color: C.ink50,
                  padding: "10px 18px",
                  textAlign: "center",
                }}
              >
                Mostrando {VISIBLE} de {filtered.length} correções.
              </div>
            )}
          </div>
        )}
      </Card>
    </Section>
  );
}

// ── 5. Evolution (over time / by version) ──────────────────────────────────────

function Sparkline({ points }: { points: { date: string; cumulative: number }[] }) {
  if (points.length < 2) return null;
  const W = 520;
  const H = 60;
  const max = Math.max(...points.map((p) => p.cumulative));
  const stepX = W / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = H - (max > 0 ? (p.cumulative / max) * (H - 6) : 0) - 3;
    return [x, y] as const;
  });
  const line = coords
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 60, display: "block" }}
      role="img"
      aria-label="Revisões acumuladas ao longo do tempo"
    >
      <path d={area} fill={C.red10} />
      <path d={line} fill="none" stroke={C.red} strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

function groupRounds(rounds: CalibrationRound[]) {
  // Preserve oldest-first order while grouping by calibration version.
  const groups: { label: string; items: CalibrationRound[] }[] = [];
  const index = new Map<string, number>();
  for (const r of rounds) {
    const label = r.sessionLabel ?? "Ajustes pontuais";
    let gi = index.get(label);
    if (gi === undefined) {
      gi = groups.length;
      index.set(label, gi);
      groups.push({ label, items: [] });
    }
    groups[gi]?.items.push(r);
  }
  return groups;
}

export function EvolutionSection({ a }: { a: CalibrationAnalytics }) {
  const e = a.evolution;
  const groups = groupRounds(e.rounds);
  const totalReviews = e.reviewsOverTime.at(-1)?.cumulative ?? 0;

  return (
    <Section
      testId="cal-data-evolution"
      title="Evolução"
      hint="Como o modelo melhorou a cada rodada de calibração, e o ritmo em que você foi acumulando revisões."
    >
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
        <Card style={{ flex: "1 1 420px" }}>
          <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink70, marginBottom: 12 }}>
            Rodadas de calibração
          </div>
          {groups.length === 0 ? (
            <Muted>
              Nenhuma rodada de calibração concluída ainda. Ao calibrar uma competência, o ganho de
              precisão aparece aqui.
            </Muted>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {groups.map((g) => (
                <div key={g.label}>
                  <div
                    style={{
                      ...UI,
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.ink50,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      marginBottom: 8,
                    }}
                  >
                    {g.label}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {g.items.map((r) => (
                      <RoundRow key={r.runId} r={r} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card style={{ flex: "1 1 320px" }}>
          <div style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink70, marginBottom: 4 }}>
            Revisões acumuladas
          </div>
          <div
            style={{
              ...DM,
              fontSize: 26,
              fontWeight: 600,
              color: C.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {totalReviews}
          </div>
          <div style={{ marginTop: 12 }}>
            {e.reviewsOverTime.length < 2 ? (
              <Muted>Acumule revisões em mais de um dia para ver a curva.</Muted>
            ) : (
              <>
                <Sparkline points={e.reviewsOverTime} />
                <div
                  style={{
                    ...UI,
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: C.ink50,
                    marginTop: 4,
                  }}
                >
                  <span>{fmtDate(e.reviewsOverTime[0]?.date)}</span>
                  <span>{fmtDate(e.reviewsOverTime.at(-1)?.date)}</span>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </Section>
  );
}

function RoundRow({ r }: { r: CalibrationRound }) {
  const improved = r.improved;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ ...UI, fontSize: 12.5, color: C.ink, flex: "1 1 auto", minWidth: 0 }}>
        {r.disciplineLabel ? `${r.disciplineLabel} · ${r.label}` : r.label}
        <span style={{ color: C.ink50 }}> · {fmtDate(r.date)}</span>
      </div>
      {r.skipped ? (
        <Chip fg={C.ink50} bg={C.ink5}>
          poucos dados
        </Chip>
      ) : (
        <span style={{ ...DM, fontSize: 12.5, color: C.ink70, fontVariantNumeric: "tabular-nums" }}>
          {fmtPct(r.baselineAccuracy)}{" "}
          <span style={{ color: improved ? C.greenFg : C.ink50 }}>{improved ? "↑" : "→"}</span>{" "}
          <strong style={{ color: improved ? C.greenFg : C.ink }}>{fmtPct(r.bestAccuracy)}</strong>
        </span>
      )}
      <Chip fg={r.applied ? C.greenFg : C.ink50} bg={r.applied ? C.greenBg : C.ink5}>
        {r.applied ? "aplicado" : "não aplicado"}
      </Chip>
    </div>
  );
}
